import type { StateObject, StateParams, StateService } from "@uirouter/angularjs";
import type { IComponentController, IScope } from "angular";
import { finalize } from "rxjs/operators";

import { first, forEach, some } from "lodash";
import type { IProfileService } from "App/Modules/Auth";
import type { IProfile, IResponseWithLoading, ISiteSlim } from "App/Modules/Models";
import { Permission } from "App/Modules/Models";
import type { ISitesRepository } from "App/Modules/Repositories";
import type { IFeatureFlagService, IPageService } from "App/Modules/Services";
import { addLoading, extendWithLoadingAndFail } from "App/Modules/Services";
import type { ISiteMenuItemsService } from "../../../Services/SiteMenu/ISiteMenuItemsService";

import type { ISiteMenuItem } from "../../../Services/SiteMenu/ISiteMenuItem";

export default class SiteLayoutComponentController implements IComponentController {
    public static $inject: string[] = [
        "$scope",
        "$state",
        "$stateParams",
        "irPageService",
        "mmSitesRepository",
        "mmProfileService",
        "irSiteMenuItemsService",
        "irFeatureFlagService",
    ];

    public site: IResponseWithLoading<ISiteSlim>;

    public siteId: string;

    public menuItems: ISiteMenuItem[];

    constructor(
        private readonly $scope: IScope,
        private readonly $state: StateService,
        private readonly $stateParams: StateParams,
        private readonly pageService: IPageService,
        private readonly sitesRepository: ISitesRepository,
        private readonly profileService: IProfileService,
        private readonly siteMenuItemService: ISiteMenuItemsService,
        private readonly featureFlagService: IFeatureFlagService,
    ) {}

    public $onInit() {
        const stateMenuItemTemplates = this.siteMenuItemService.get();

        this.$scope.$on("updateSiteTitle", (event, ...args) => {
            this.site.data.name = first(args);
        });

        if (this.profileService.hasPermission(Permission.SiteInformation)) {
            extendWithLoadingAndFail(this.sitesRepository.getSiteSlim(this.siteId))
                .pipe(
                    addLoading(this.pageService),
                    finalize(() => this.$scope.$applyAsync()),
                )
                .subscribe(
                    (result) => {
                        this.site = result;
                    },
                    (result) => {
                        this.site = result;
                    },
                );

            this.menuItems = this.createMenu(
                this.$state.$current,
                stateMenuItemTemplates.filter((stateMenuItem) => {
                    if (stateMenuItem.featureSwitcherId) {
                        return this.featureFlagService.getFlag(stateMenuItem.featureSwitcherId);
                    }
                    return stateMenuItem;
                }),
            );
        }
    }

    public preventDeselect($event: JQueryEventObject) {
        if (!$event) {
            return;
        }
        $event.preventDefault();
    }

    private createMenu(state: StateObject, menuItemTemplates: ISiteMenuItem[]): ISiteMenuItem[] {
        const tmpMenuItems: ISiteMenuItem[] = [];
        const profile = this.profileService.getProfileIfAvailable();

        forEach(menuItemTemplates, (itemTemplate) => {
            let menuItem: ISiteMenuItem = null;
            if (this.hasPermission(itemTemplate, profile)) {
                menuItem = {
                    caption: itemTemplate.caption,
                    state: itemTemplate.state,
                } as ISiteMenuItem;
                tmpMenuItems.push(menuItem);
            }
        });

        return tmpMenuItems;
    }

    private hasPermission(menuItem: ISiteMenuItem, profile: IProfile): boolean {
        if ((menuItem.permission as { partnerPermission: Permission }).partnerPermission) {
            const { partnerPermission } = menuItem.permission as {
                partnerPermission: Permission;
            };
            return profile && some(profile.userPermissions.partnerPermissions, (p) => p === partnerPermission);
        }
        if ((menuItem.permission as { sitePermission: Permission }).sitePermission) {
            const { sitePermission } = menuItem.permission as {
                sitePermission: Permission;
            };
            return (
                profile &&
                (some(profile.userPermissions.sitePermissions, (p) => p === sitePermission) ||
                    some(profile.userPermissions.partnerPermissions, (p) => p === sitePermission))
            );
        }

        return false;
    }

    private featureSwitchMenuItemTemplates(stateMenuItemTemplates: ISiteMenuItem[]) {
        return stateMenuItemTemplates.filter((stateMenuItem) => {
            if (stateMenuItem.featureSwitcherId) {
                return this.featureFlagService.getFlag(stateMenuItem.featureSwitcherId);
            }
            return stateMenuItem;
        });
    }
}
